import {
  AlertPosition,
  AlertType,
  IAlert,
  IEntityMetaData,
  INotificationAction,
  INotificationActionQueryResult,
  INotificationActionQueryVariables,
  Maybe,
} from '../graphql/schema/p4types';
import { useP4ToastController } from '../views/system/NotificationsArea';
import { useLazyQuery } from '@apollo/client';
import NotificationActionQuery from '../graphql/queries/actions/NotificationActionQuery.graphql';
import React, { useCallback, useEffect, useState } from 'react';
import { ActionNotification } from '../components/Notification';
import { IJobMeta } from './BackgroundJobs';

export type IBlockingAlert = {
  title: string;
  message: string;
  type: AlertType;
};

function isIAlert(alert: Maybe<IAlert>): alert is IAlert {
  return alert !== undefined;
}

const useProcessJobActions = () => {
  const { dispatchJobToast, dispatchSimpleToast } = useP4ToastController();

  return useCallback(
    (entityMetaData: Maybe<IEntityMetaData>) => {
      if (entityMetaData && entityMetaData.actions && Array.isArray(entityMetaData.actions)) {
        const onComplete = () => {
          console.warn("No 'onComplete' is defined for entityMetaData jobs - Show an alert?");
        };

        entityMetaData.actions
          .filter((action) => action?.jobInfo && action?.actionType === 'JOB_INFO')
          .forEach((action) => {
            const job = action?.jobInfo;
            const title = job?.description || 'No Title';

            if (job && job.jobId && !job.failed) {
              const jobMeta: IJobMeta = { ...job, title, onComplete };
              dispatchJobToast(jobMeta);
            } else {
              dispatchSimpleToast(
                'Job Error',
                `Job could not be started${job && job.message ? ` - ${job.message}` : ''}`,
                AlertType.Error
              );
            }
          });
      }
    },
    [dispatchJobToast, dispatchSimpleToast]
  );
};

/**
 * Extracted processAlerts to hook so we can access various additional functionality.
 * Returns a function to process entityMetaData, dispatching any alerts in it to the Toaster.
 */
export const useProcessAlerts = ({ addHeaderAlert }: { addHeaderAlert?: (alert: IAlert) => void } = {}) => {
  const [getActionNotificationData] = useLazyQuery<INotificationActionQueryResult, INotificationActionQueryVariables>(
    NotificationActionQuery
  );

  const { dispatchToast, dispatchSimpleToast, dispatchBlockingToast, dismissToast } = useP4ToastController();

  const processJobActions = useProcessJobActions();

  return useCallback(
    (entityMetaData: Maybe<IEntityMetaData>, defaultTitle = '---') => {
      processJobActions(entityMetaData);

      const newBlockingAlerts: IBlockingAlert[] = [];

      if (entityMetaData && entityMetaData.alerts && Array.isArray(entityMetaData.alerts)) {
        entityMetaData.alerts.filter(isIAlert).forEach((alert) => {
          const { title, message, alertType, alertPosition, notificationActionId, notificationActionType } = alert;
          if (alertType === AlertType.Action) {
            getActionNotificationData({ variables: { id: notificationActionId, type: notificationActionType } }).then(
              (result) => {
                const toastId = `${notificationActionType}-${notificationActionId}`;

                const dismiss = () => {
                  dismissToast(toastId);
                };

                if (result?.data?.data) {
                  dispatchToast(
                    <ActionNotification
                      title={title ?? defaultTitle}
                      message={message}
                      type={AlertType.Action}
                      key={toastId}
                      dismiss={dismiss}
                      notificationData={result.data.data as INotificationAction}
                    />,
                    {
                      intent: 'info',
                      timeout: -1,
                      toastId: toastId,
                      title: title ?? defaultTitle,
                      type: AlertType.Action,
                    }
                  );
                }
              }
            );
          } else if (alertPosition === AlertPosition.Header) {
            addHeaderAlert?.(alert);
          } else if (alertPosition === AlertPosition.Block) {
            const alertTitle = title ?? `${defaultTitle} Alert`;
            const alertMessage = message as string;

            const isDuplicate = newBlockingAlerts.some(
              (a) => a.message.trim() === alertMessage.trim() && a.title.trim() === alertTitle.trim()
            );

            if (!isDuplicate) {
              newBlockingAlerts.push({
                title: alertTitle,
                message: alertMessage,
                type: alertType as AlertType,
              });
            }
          } else {
            dispatchSimpleToast(title || `${defaultTitle} Alert`, message as string, alertType as AlertType);
          }
        });
      }

      if (newBlockingAlerts?.length > 0) {
        const combinedAlerts: IBlockingAlert[] = Object.values(
          newBlockingAlerts.reduce<Record<string, IBlockingAlert>>((acc, alert) => {
            const title = alert.title.trim();

            if (acc[title]) {
              acc[title].message += `\n ${alert.message}`;
            } else {
              acc[title] = { ...alert };
            }
            return acc;
          }, {})
        );

        combinedAlerts.forEach((blockingAlert) => {
          const { title, message, type } = blockingAlert;
          dispatchBlockingToast(title, message, type);
        });
      }
    },
    [
      addHeaderAlert,
      dismissToast,
      dispatchBlockingToast,
      dispatchSimpleToast,
      dispatchToast,
      getActionNotificationData,
      processJobActions,
    ]
  );
};
